import { Button, ButtonProps, CircularProgress, SxProps, Theme, useTheme } from "@mui/material";
import { IconType } from "react-icons";
import IconWrapper from "./IconWrapper";

type ActionButtonWithIconProps = {
    text: string;
    icon: IconType;
    onClick: () => void;
    isLoading?: boolean;
    buttonProps?: ButtonProps; // Additional props for the MUI Button
    sx?: SxProps<Theme>; // Optional custom styling
};

/**
 * ActionButtonWithIcon
 *
 * Renders a stylized pill-shaped button containing an icon and an expandable label.
 * The label animates in on hover, providing a clean, compact UI for action buttons.
 * Uses MUI's `Button` component and integrates theming via the `useTheme` hook.
 *
 * Features:
 * - Collapsed button with just an icon by default
 * - Expands on hover to reveal the `text` label
 * - Fully theme-aware (colors, hover effects, font styles)
 * - Rounded "chip" style for modern UI aesthetics
 * - Keyboard accessible with smooth transitions
 * - Optional loading state showing a spinner instead of the icon
 *
 * Props:
 * - `text` (string) - The label displayed next to the icon.
 * - `icon` (IconType) - The icon element shown before the text (e.g. `<ScienceIcon />`).
 * - `onClick` (function) - Function to call when the button is clicked.
 * - `isLoading (boolean)` - A variable that shows if the button's clicking event loading
 * - `buttonProps` (ButtonProps) - Additional props for the MUI Button component.
 * - `sx` (SxProps<Theme>) - Optional custom styles for the button.
 *
 * @returns {JSX.Element} The rendered action button with icon and text.
 *
 * @example
 * <ActionButtonWithIcon
 *      text="Fájlok feltöltése"
 *      icon={BsUpload}
 *      onClick={openFileDrawer}
 *      isLoading={false}
 * />
 */
function ActionButtonWithIcon({ text, icon, onClick, isLoading, buttonProps, sx }: ActionButtonWithIconProps) {
    const theme = useTheme();

    const Icon = IconWrapper(icon);

    const size = 40;

    return (
        <Button
            onClick={onClick}
            {...buttonProps}
            sx={{
                display: "flex",
                alignItems: "center",
                backgroundColor: theme.palette.actionButtonBackground,
                color: theme.palette.text.primary,
                px: 1.5,
                py: 0.5,
                borderRadius: "9999px",
                boxShadow: 1,
                overflow: "hidden",
                transition: "all 0.3s ease",
                minWidth: size,
                minHeight: size,
                textTransform: "none",
                "&:hover": {
                    background: theme.palette.actionButtonBackgroundHover,
                    transition: "background 0.3 ease"
                },
                "& .icon": {
                    minWidth: "1.1rem",
                    minHeight: "1.1rem",
                    width: "1.1rem",
                    height: "1.1rem",
                    transition: "transform 0.3s ease"
                },
                "& .text-wrapper": {
                    maxWidth: 0,
                    overflow: "hidden",
                    transition: "max-width 0.3s ease, margin-left 0.3s ease",
                    ml: 0
                },
                "&:hover .text-wrapper": {
                    maxWidth: "220px",
                    ml: 1
                },
                "& .text": {
                    opacity: 0,
                    whiteSpace: "nowrap",
                    fontSize: "0.9375rem",
                    fontWeight: 500,
                    transition: "opacity 0.3s ease"
                },
                "&:hover .text": {
                    opacity: 1
                },
                ...sx
            }}
        >
            {isLoading ? <CircularProgress size={18} thickness={4} className="icon" color="inherit" /> : <Icon className="icon" />}
            <div className="text-wrapper">
                <span className="text">{text}</span>
            </div>
        </Button>
    );
}

export default ActionButtonWithIcon;
